import { environment } from '@src/environments/environment';
import { Component, ElementRef, OnInit, ViewChild, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Table } from 'primeng/table';
import { StatusPipe } from '@src/app/shared/pipes/status.pipe';
import { ConfirmationService } from 'primeng/api';
import { CompanyResponse, RetailerDetailsResponse, RetailersService } from '@src/app/shared/backend/clients';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import * as maplibregl from 'maplibre-gl';
import { CountryService } from '@src/app/shared/services/countries-service';
import { PermissionService } from '@src/app/shared/services/permission.service';
import { Actions } from '@src/app/shared/backend/auth/Action';
import { UserRolesService } from '@src/app/shared/services/userRoles.service';
import { SessionService } from '@src/app/shared/services/session.service';
import { DialogReasonComponent } from '@src/app/shared/components/dialog-reason/dialog-reason.component';
import { BaseViewComponent } from '@src/app/shared/components/base-list/base-view.component';
import { DynamicAttributeDefinition } from '@src/app/shared/components/attribute-values-form/attribute-values-form.model';
import { RetailerAttributeDisplayItem } from '../retailer-attribute-display.model';
import { RetailerAttributeValuesAdapter } from '../retailer-attribute-values.adapter';
import { Features } from '@src/app/shared/backend/auth/Feature';

@Component({
    selector: 'app-view-retailer',
    templateUrl: './view-retailer.component.html',
    styleUrl: './view-retailer.component.scss',
    host: {
        class: 'app-view-retailer'
    },
    providers: [StatusPipe, ConfirmationService],
    standalone: false
})
export class ViewRetailerComponent extends BaseViewComponent implements OnInit, OnDestroy {

  private readonly destroyedComponent$ = new Subject<void>();

  @ViewChild('dialog') dialog: DialogReasonComponent | undefined;

  public delete: boolean = false;
  public allowManageUsers: boolean = false;
  public readUser: boolean = false;
  public allowRemoveUser: boolean = false;
  public export: boolean = false;
  public allowUpdateRetailer: boolean = false;
  public allowViewRetailerType: boolean = false;
  public allowViewBusinessGroup: boolean = false;
  public allowViewMarket: boolean = false;
  public allowViewDistributionChannel: boolean = false;
  public showDistributionChannel: boolean = false;

  retailerId: number = 0;
  retailerUsersData: any;
  retailerAttributesFeatureEnabled = false;
  showRetailerAttributeDetails = false;
  retailerAttributeDefinitions: DynamicAttributeDefinition[] = [];
  retailerAttributeDisplayItems: RetailerAttributeDisplayItem[] = [];
  retailer: RetailerDetailsResponse = {
    id: 0,
    code: '',
    name: '',
    street: '',
    zip: '',
    city: '',
    isoCountry: '',
    isActive: false,
    hasCpo: false,
    market: {
      id: 0,
      name: '',
      code: ''
    },
    createdOn: ''
  };
  countries: any[] = [];

  @ViewChild('dtRetailerUsersTable') table: Table | undefined;

  map: maplibregl.Map | undefined;
  isValidLocation: boolean = false;
  shouldInitMap: boolean = false;

  private mapContainerEl?: ElementRef<HTMLElement>  | null = null;

  @ViewChild('mapContainer') set mapContainer(el: ElementRef<HTMLElement> | null) {
    this.mapContainerEl = el;
    if (el && this.shouldInitMap) {
      this.initializeMap();
    }
  }

  stateOptions: any[];
  stateInitialValue: any;
  stateNewValue: any;
  sanitizedConfirmMessage: SafeHtml | undefined;
  public company: CompanyResponse | undefined;

  retailerUserStatuses: any[] | undefined;

  displayUserFromRetailerModalbox: boolean = false;
  selectedRetailerUserId: string | undefined;

  companyHasCpo: boolean = false;

  public userRemovalForm: FormGroup = this.fb.group({
    removalReason: ['', [Validators.required, Validators.maxLength(128)]],
  });

  constructor(
    private permissionService: PermissionService,
    private router: Router,
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer,
    private translate: TranslateService,
    private statusPipe: StatusPipe,
    public userRoleService: UserRolesService,
    private fb: FormBuilder,
    private countryService: CountryService,
    private retailersService: RetailersService,
    private session: SessionService,
    private confirmationService: ConfirmationService,
    private retailerAttributeValuesAdapter: RetailerAttributeValuesAdapter,
    private changeDetector: ChangeDetectorRef) {
    super();
    this.updateStateOptions();
  }

  ngOnInit() {
    this.translate.onLangChange
      .pipe(takeUntil(this.destroyedComponent$))
      .subscribe(() => {
        this.updateStateOptions();
        this.buildRetailerAttributeDisplayItems();
      });

    this.init();
  }

  private async init(): Promise<void> {
    const settings = await this.session.getCompanySettings();

    this.retailerAttributesFeatureEnabled =
      settings.features?.some(
        feature =>
          feature.name === 'RetailerAttributes' && feature.isEnabled
      ) ?? false;

    this.company = await this.session.getCompanyData();

    this.showRetailerAttributeDetails = this.company?.showRetailerAttributeDetails ?? false;

    this.countries = await this.countryService.getCountries();
    this.companyHasCpo = !!this.company?.hasCpo;

    await this.loadRetailerAttributeDefinitions();

    this.route.params
      .pipe(takeUntil(this.destroyedComponent$))
      .subscribe(async params => {
        this.retailerId = Number(params['retailerId']);

        if (this.retailerId > 0) {
          await this.get();
        }
      });

    this.delete = await this.permissionService.hasActionPermissions(Actions.delete);

    this.getStatuses();

    this.allowUpdateRetailer = await this.permissionService.hasActionPermissions(Actions.modify_retailers);
    this.export = await this.permissionService.hasActionPermissions(Actions.export_retailers);
    this.allowManageUsers = await this.permissionService.hasActionPermissions(Actions.manage_retailers_users);
    this.allowViewRetailerType = await this.permissionService.hasActionPermissions(Actions.view_retailertypes);
    this.allowViewMarket = await this.permissionService.hasActionPermissions(Actions.view_markets);
    this.allowViewDistributionChannel = await this.permissionService.hasActionPermissions(Actions.view_distributionchannels);
    this.showDistributionChannel = await this.permissionService.hasFeaturePermissions(Features.DistributionChannels);

    maplibregl.setWorkerUrl('/assets/maplibre/maplibre-gl-worker.mjs');

    if (this.shouldInitMap && this.mapContainer) {
      this.initializeMap();
    }

    this.changeDetector.detectChanges();
  }

  private async loadRetailerAttributeDefinitions(): Promise<void> {
    const shouldLoadDefinitions = this.retailerAttributesFeatureEnabled && this.showRetailerAttributeDetails;

    if (!shouldLoadDefinitions) {
      this.retailerAttributeDefinitions = [];
      return;
    }

    try {
      this.retailerAttributeDefinitions = await this.retailerAttributeValuesAdapter.getActiveDefinitions();
    } catch (error) {
      console.error('Error loading retailer attribute definitions:', error);
      this.retailerAttributeDefinitions = [];
    }
  }

  private updateStateOptions(): void {
    this.stateOptions = [
      {
        label: this.translate.instant('BOOLEANS.ACTIVATE_SWITCH'),
        displayLabel: this.translate.instant('BOOLEANS.ACTIVATE_SWITCH'),
        value: true
      },
      {
        label: this.translate.instant('BOOLEANS.DEACTIVATE_SWITCH'),
        displayLabel: this.translate.instant('BOOLEANS.DEACTIVATE_SWITCH'),
        value: false
      }
    ];
  }

  initStateSwitch() {
    this.stateInitialValue = this.retailer.isActive;

    const selectedLabel = this.stateOptions.find(option => option.value === this.stateInitialValue)?.label.toLowerCase();
    const translatedMessage = this.translate.instant('MODALBOXES.CONFIRM_STATE_CHANGE', { state: selectedLabel });
    this.sanitizedConfirmMessage = this.sanitizer.bypassSecurityTrustHtml(translatedMessage);
  }

  onStateChange(event: any) {
    this.stateNewValue = event.value;
    const selectedLabel = this.stateOptions.find(option => option.value === this.stateNewValue)?.label.toLowerCase();

    const translatedMessage = this.translate.instant('MODALBOXES.CONFIRM_STATE_CHANGE', { state: selectedLabel });
    this.sanitizedConfirmMessage = this.sanitizer.bypassSecurityTrustHtml(translatedMessage);

    // State update confirmation dialog
    this.confirmationService.confirm({
      key: 'confirm-state-change-dialog',
      header: this.translate.instant('MODALBOXES.TITLE_CONFIRM_STATE_CHANGE')
    });
  }

  override async get() {
    try {
      this.retailer = await firstValueFrom(this.retailersService.apiRetailersDetailsIdGet(this.retailerId));
      this.retailerUsersData = this.retailer.users;

      this.buildRetailerAttributeDisplayItems();

      // validate geodata before map init
      const latitude = parseFloat(this.retailer.latitude ?? '');
      const longitude = parseFloat(this.retailer.longitude ?? '');

      if (this.isValidCoordinates(latitude, longitude)) {
        this.isValidLocation = true;
        this.shouldInitMap = true;

        // force change detection
        //this.changeDetector.detectChanges();
        if (this.mapContainer) {
          this.initializeMap();
        }

      } else {
        this.isValidLocation = false;
        this.shouldInitMap = false;
        this.map?.remove();

        console.warn('Invalid or missing geodata:', {
          latitude,
          longitude,
          retailerId: this.retailerId
        });
      }

      this.initStateSwitch();

    } catch (error) {
      console.log('Error fetching retailer data:', error);
      this.isValidLocation = false;
    } finally {
      this.changeDetector.detectChanges();
    }
  }

  private initializeMap(): void {
    const mapContainerRef = this.mapContainerEl;
    if (mapContainerRef == null) {
      return
    }

    const latitude = parseFloat(this.retailer.latitude ?? '');
    const longitude = parseFloat(this.retailer.longitude ?? '');

    if (!this.isValidCoordinates(latitude, longitude)) {
      console.warn('Skipped map init, invalid geodata');
      this.isValidLocation = false;
      return;
    };

    // prevent leaked renders / duplicated maps
    this.map?.remove();

   this.map = new maplibregl.Map({
      container: mapContainerRef.nativeElement,
      style: `https://maps.geo.${environment.awsRegion}.amazonaws.com/v2/styles/Standard/descriptor?key=${environment.awsMapApiKey}`,
      center: [longitude, latitude],
      zoom: 15,
      validateStyle: false
    });
    this.map.addControl(new maplibregl.FullscreenControl());

    this.map.on('load', () => {
      this.map?.resize();
      this.addMarker()});

    this.map.on('error', (error: any) => {
      console.error('AWS Map error:', error);
    });
  }

  private addMarker() {
    if (!this.map) {
      return;
    }

    const isoCountry = this.retailer.isoCountry?.toLowerCase();
    const selectedCountry = this.countries.find(country => {
      if (country.alpha2.toLowerCase() === isoCountry) {
        return country;
      }
    });

    const openingLabel = this.translate.instant('RETAILER_SINGLE_VIEW.OPENING_DATE');
    const closingLabel = this.translate.instant('RETAILER_SINGLE_VIEW.CLOSING_DATE');
    const openingValue = this.retailer.openingDate ? new Date(this.retailer.openingDate).toLocaleDateString() : (this.translate.instant('NO_DATA.NO_DATA_AVAILABLE'));
    const closingValue = this.retailer.closingDate ? new Date(this.retailer.closingDate).toLocaleDateString() : (this.translate.instant('NO_DATA.NO_DATA_AVAILABLE'));

    const countryName = selectedCountry?.name ?? '-';

    const popupContent = `
      <h3>${this.retailer.name}</h3>
      <p>${this.retailer.street}, ${this.retailer.zip} ${this.retailer.city}, ${countryName}</p>
      <p>${openingLabel}: ${openingValue}</p>
      <p>${closingLabel}: ${closingValue}</p>
    `;

    const popup = new maplibregl.Popup({ offset: 25 }).setHTML(popupContent);

    const latitude = parseFloat(this.retailer.latitude ?? '');
    const longitude = parseFloat(this.retailer.longitude ?? '');

    new maplibregl.Marker()
      .setLngLat([longitude, latitude])
      .setPopup(popup)
      .addTo(this.map);
  }

  manageRetailerUsers() {
    this.router.navigate([`/retailers/${this.retailerId}/manage-users`]);
  }

  goToEditRetailer() {
    this.router.navigate([`/retailers/${this.retailerId}/edit`]);
  }

  goToUserDetails(event: any, userId: string) {
    this.router.navigate([`/users/${userId}`]);
  }

  async onRemoveUserFromRetailer(event: any, userId: string) {
    this.selectedRetailerUserId = userId;
    this.userRemovalForm.reset();
    this.displayUserFromRetailerModalbox = true;
    await this.get();
  }

  onRemoveUser() {
    if (this.userRemovalForm.valid) {
      // console.log(`User ID to be removed: ${this.selectedRetailerUserId}`);
      // console.log(`Removal Reason: ${this.userRemovalForm.get('removalReason')?.value}`);

      // To-DO: Call remove user from retailer endpoint
      // ...

      this.displayUserFromRetailerModalbox = false;
      this.selectedRetailerUserId = '';
    }
  }

  onCancelRemoval() {
    this.displayUserFromRetailerModalbox = false;
    this.selectedRetailerUserId = '';
  }

  onConfirmStateChange(): void {
    if (this.stateNewValue === false) {
      this.retailersService.apiRetailersIdDeactivatePut(this.retailerId).subscribe({
        next: async () => {
          await this.get();
        },
        error: (error) => {
          console.error('Failed to deactivate retailer', error);
        }
      });
    }
    if (this.stateNewValue === true) {
      this.retailersService.apiRetailersIdActivatePut(this.retailerId).subscribe({
        next: async () => {
          await this.get();
        },
        error: (error) => {
          console.error('Failed to activate retailer', error);
        }
      });
    }
    this.confirmationService.close();
  }

  onCancelStateChange() {
    // Revert state change
    const nonMatchingOption = this.stateOptions.find(option => option.value !== this.stateInitialValue).value;
    this.stateInitialValue = nonMatchingOption;
    this.confirmationService.close();
  }

  private getStatuses() {
    this.retailerUserStatuses = this.statusPipe.getList();
  }

  private isValidCoordinates(latitude: number, longitude: number): boolean {
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      return false;
    }

    // Check for valid ranges
    if (latitude < -90 || latitude > 90) {
      return false;
    }
    if (longitude < -180 || longitude > 180) {
      return false;
    }

    if (latitude === 0 && longitude === 0) {
      return false;
    }

    return true;
  }

  async showDeleteDialog(): Promise<void> {
    const deleteCheckResponses = await firstValueFrom(this.retailersService.apiRetailersDeleteCheckBatchPost([this.retailerId]));

    this.dialog?.show(deleteCheckResponses);
  }

  async deleteRetailer(doDelete: boolean): Promise<void> {
    if(doDelete){
      await firstValueFrom(this.retailersService.apiRetailersDeleteIdDelete(this.retailerId));
      this.router.navigate([`/retailers`]);
    }
  }

  private buildRetailerAttributeDisplayItems(): void {
    const shouldDisplayAttributes = this.retailerAttributesFeatureEnabled && this.showRetailerAttributeDetails;

    if (!shouldDisplayAttributes) {
      this.retailerAttributeDisplayItems = [];
      return;
    }

    const currentLanguage = this.translate.currentLang || 'en';

    this.retailerAttributeDisplayItems = this.retailerAttributeValuesAdapter.mapDetailsToDisplayItems(this.retailerAttributeDefinitions, this.retailer.retailerAttributeValues, currentLanguage);
  }

  ngOnDestroy(): void {
    this.destroyedComponent$.next();
    this.destroyedComponent$.complete();
    this.map?.remove();
  }
}
